import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Info, Phone, Mail, MapPin, Globe, MessageSquare } from "lucide-react";

export default function Layout({ children, pageTitle = "Dashboard", backTo, background }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);

  const isRegistrationPage = location.pathname === "/registration" || pageTitle === "Registration";

  // Site contact array
  const siteContact = {
    mobilenumber: "+94 77 141 6733",
    whatsapp: "+94 77 141 6733",
    email: "info@woodchaletsella.com.lk",
    address: "Ella, Sri Lanka",
    website: "woodchaletsella.com.lk",
  };

  const handleBackdropClick = (e) => {
    if (e.target.id === "backdrop") {
      setModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 text-gray-900">
      {/* Header */}
      <header className="flex justify-between items-center px-2 py-3 sm:px-4 sm:py-2  mx-auto w-full border-b border-gray-300 bg-white">
        {/* Back Button */}
        <button
          onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
          aria-label="Go Back"
          className="p-2 rounded-md hover:bg-gray-200 transition"
        >
          <ChevronLeft className="h-6 w-6 text-gray-600" /> {/* Changed icon color */}
        </button>

        {/* Page Title */}
        <h1 className="text-xl sm:text-xl font-semibold truncate text-center flex-grow select-none">
          {pageTitle}
        </h1>

        {/* Info Icon */}
        <button
          onClick={() => setModalOpen(true)}
          aria-label="Open Information"
          className="p-2 rounded-md hover:bg-gray-200 transition"
        >
          <Info className="h-6 w-6 text-gray-600" /> {/* Changed icon color */}
        </button>
      </header>

      {/* Info Modal */}
      {modalOpen && (
        <div
          id="backdrop"
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-end sm:items-center justify-center"
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-xl shadow-lg p-6 w-full max-w-sm space-y-4 relative transition-all duration-300 ease-in-out"
            style={{
              transform: modalOpen ? "translateY(0)" : "translateY(100%)",
              opacity: modalOpen ? 1 : 0,
            }}
          >
            <h2 className="text-lg font-bold text-gray-800">Contact Information</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-600" /> {/* Changed icon color */}
                <a
                  href={`tel:${siteContact.mobilenumber.replace(/\s/g, "")}`}
                  className="text-gray-600 no-underline"
                >
                  {siteContact.mobilenumber}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-lime-600" /> {/* Changed icon color */}
                <a
                  href={`https://wa.me/${siteContact.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-600 no-underline"
                >
                  {siteContact.whatsapp}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />{" "} {/* Changed icon color */}
                <a
                  href={`mailto:${siteContact.email}`}
                  className="text-gray-600 no-underline"
                >
                  {siteContact.email}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-600" />{" "} {/* Changed icon color */}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteContact.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-600 no-underline"
                >
                  {siteContact.address}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-orange-600" /> {/* Changed icon color */}
                <a
                  href={`https://${siteContact.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-600 no-underline"
                >
                  {siteContact.website}
                </a>
              </p>
            </div>
            
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
            >
              ×
            </button>
          </div>
        </div>
      )}

 {/* Main Content */}
 <main className={`flex-grow max-w-7xl mx-auto py-6 w-full ${isRegistrationPage ? '' : 'px-4 sm:px-6'}`}>
        {
          !background ? (
            <div className={`bg-white shadow-md rounded-lg ${isRegistrationPage ? 'p-4' : 'p-5'} hover:shadow-lg transition`}>
              {children}
            </div>
          ) : (
            children
          )
        }
      </main>

      {/* Footer */}
      <footer className="p-6 bg-white shadow-inner text-center text-sm text-gray-500">
        <div className="max-w-7xl mx-auto">
          © {new Date().getFullYear()} Wood Chalets Ella. All rights reserved.
        </div>
        <div className="max-w-7xl mx-auto">
        Design & Development <a href="https://akasa.lk">@Akasa.lk</a>
        </div>
      </footer>
    </div>
  );
}